import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddAddressesPopup from "pages/community/Addresses/AddAddressesPopup";
import AddressesDetail from "pages/community/Addresses/AddressesDetail";

import { addressesAPI } from "api/Community/AddressesAPI";
import { Address } from "api/Community/CommunityInterfaces";

const Addresses: React.FC = () => {
  const [data, setData] = useState<Address[]>([]);
  const [selected, setSelected] = useState<Address | null>(null);
  const [loading, setLoading] = useState(true);

  /* -------------------- URL hooks -------------------- */
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  /* -------------------- Load all addresses -------------------- */
  useEffect(() => {
    addressesAPI
      .getAll()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  /* -------------------- Active ID from URL -------------------- */
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  /* -------------------- Navigate on selection -------------------- */
  const handleSelect = (id: number) => {
    navigate(`/community/addresses/${id}/`);
  };
  
  /* -------------------- Load selected record when URL changes -------------------- */
  useEffect(() => {
    if (!activeId) {
      setSelected(null);
      return;
    }

    addressesAPI
      .getById(activeId)
      .then((detail) => setSelected(detail))
      .catch(() => setSelected(null));
  }, [activeId]);

  /* -------------------- Loading Spinner -------------------- */
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <GeneralLayout
      useLeftDrawer
      defaultOpenLeft={true}
      leftDrawerContent={
        <GenericRecordSelection<Address>
          items={data}
          onSelect={(a) => handleSelect(a.id)}
          selectedId={activeId ?? null}
          AddPopupComponent={AddAddressesPopup}
          onCreated={(newAddress) => {
            setData((prev) => [...prev, newAddress]);
            navigate(`/community/addresses/${newAddress.id}/`);
          }}
          idKey="id"
          nameKey={(a) =>
            `${a.street_address ? a.street_address + ", " : ""}${a.city || ""}`
          }
          sortKey="street_address"
          colorKey="communityPrimary"
          searchPlaceholder="Search addresses..."
        />
      }
    >
      <AddressesDetail
        address={selected}
        onAddressUpdated={(updated) => setSelected(updated)}
        onAddressesCreated={(newAddress) => {
          setData((prev) => [...prev, newAddress]);
          navigate(`/community/addresses/${newAddress.id}/`);
        }}
        onClose={() => setSelected(null)}
      />
    </GeneralLayout>
  );
};

export default Addresses;
